/**
 * SN-4838 — what an import's 409 says happened, read once so the import modals
 * word every pass the same way.
 *
 * A first upload (conflicts=check) that finds possible duplicates imports
 * nothing: the duplicates are reviewed first. When the same file also has
 * problem rows, the modal used to show only the problems under "Oops! Unable to
 * finish your import.", drop the duplicates, and never say nothing had been
 * imported.
 */

/** One failed line as `failed.errors` lists it: a message per field, and `line`. */
export type ImportErrorEntry = Readonly<Record<string, unknown>>

/** The "Download CSV with failed rows" file the import returns. */
export interface ImportFailedRowsFile {
  name: string
  rows: string
}

export type ImportReportKind =
  /** Only possible duplicates: the review opens. */
  | 'duplicates'
  /** Problem rows and possible duplicates: nothing is imported until the review. */
  | 'problemsAndDuplicates'
  /** Rows (or the header) failed and nothing was imported. */
  | 'nothingImported'
  /** Some rows were imported and the problem rows were not. */
  | 'partlyImported'
  | 'none'

export interface ImportReport {
  kind: ImportReportKind
  importedCount: number
  /** The failed lines, numbered as in the file the admin uploaded. */
  problems: ImportErrorEntry[]
  /** How many lines failed; each line counts once. */
  problemRowCount: number
  /** The rows to review (`conflicts.conflicts`), as the import sent them. */
  duplicates: unknown[]
  failedRowsFile: ImportFailedRowsFile | null
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const countOf = (section: unknown): number => {
  const count = isRecord(section) ? section.count : undefined

  return typeof count === 'number' && Number.isFinite(count) && count > 0
    ? count
    : 0
}

const listOf = (section: unknown, key: string): unknown[] => {
  const list = isRecord(section) ? section[key] : undefined

  return Array.isArray(list) ? list : []
}

const failedRowsFileOf = (file: unknown): ImportFailedRowsFile | null =>
  isRecord(file) &&
  typeof file.name === 'string' &&
  typeof file.rows === 'string' &&
  file.rows !== ''
    ? { name: file.name, rows: file.rows }
    : null

/**
 * The line of the uploaded file that a confirm's report calls `line`. The
 * confirm posts the file without the rows the review ignored, so every line
 * after one of them comes back one lower.
 */
export const originalLine = (
  line: number,
  droppedLines: readonly number[]
): number => {
  let original = line
  const dropped = [...new Set(droppedLines)].sort((a, b) => a - b)

  for (const droppedLine of dropped) {
    if (droppedLine <= original) {
      original += 1
    }
  }

  return original
}

/**
 * Read a 409's body. `droppedLines` are the lines the duplicate review left out
 * of the file it posted. Never throws: anything unexpected reads as nothing.
 */
export const readImportReport = (
  body: unknown,
  droppedLines: readonly number[] = []
): ImportReport => {
  const response = isRecord(body) ? body : {}

  const problems = listOf(response.failed, 'errors')
    .filter(isRecord)
    .map((entry) =>
      typeof entry.line === 'number'
        ? { ...entry, line: originalLine(entry.line, droppedLines) }
        : entry
    )
  const problemRowCount =
    problems.length > 0
      ? new Set(problems.map(({ line }) => line)).size
      : countOf(response.failed)
  const duplicates = listOf(response.conflicts, 'conflicts')
  const importedCount = countOf(response.success)

  const hasProblems = problemRowCount > 0
  const hasDuplicates = duplicates.length > 0

  let kind: ImportReportKind = 'none'
  if (hasProblems && hasDuplicates) {
    kind = 'problemsAndDuplicates'
  } else if (hasDuplicates) {
    kind = 'duplicates'
  } else if (hasProblems) {
    kind = importedCount > 0 ? 'partlyImported' : 'nothingImported'
  }

  return {
    kind,
    importedCount,
    problems,
    problemRowCount,
    duplicates,
    failedRowsFile: hasProblems ? failedRowsFileOf(response.file) : null,
  }
}
